import { FocusMonitor } from "@angular/cdk/a11y";
import { Directionality } from "@angular/cdk/bidi";
import { KeyValue } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  Optional,
  Output,
  SimpleChanges,
  forwardRef,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { NzConfigService } from "ng-zorro-antd/core/config";
import { NzSwitchComponent } from "ng-zorro-antd/switch";
import { UiToggleSize, UiToggleType } from "@webapp/ui/toggle/toggle.models";

/**
 * This component extends the base NzSwitch component of NG-Zorro.
 * Example usage : https://ng.ant.design/components/switch/en.
 * DS epic : https://gtmhub.atlassian.net/browse/GVS-14884.
 * Confluence page : https://gtmhub.atlassian.net/l/cp/3hd2uvsK
 *
 * @preserve nz-params, ngModel, ReactiveForms
 * @param uiType UiToggleType (default: 'toggle')
 * @param uiSize UiToggleSize (default: 'default')
 * @param disabled boolean (default: false)
 * @param uiLoading boolean (default: false)
 * @param value boolean - legacy usage
 * @param key string - legacy usage
 * @param toggled EventEmitter<{ key: string; value: boolean }> - legacy usage
 */

@Component({
  selector: "ui-toggle",
  templateUrl: "./toggle.component.html",
  styleUrls: ["./toggle.component.less"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UiToggleComponent),
      multi: true,
    },
  ],
})
export class UiToggleComponent extends NzSwitchComponent implements ControlValueAccessor, OnInit, OnChanges {
  @Input("uiId")
  public nzId: string | null = null;
  @Input("uiType")
  public toggleType?: UiToggleType = "toggle";
  @Input("uiSize")
  public nzSize: UiToggleSize = "default";
  @Input("disabled")
  public nzDisabled = false;
  @Input("uiLoading")
  public nzLoading = false;

  /* legacy behavior */
  @Input()
  public tabindex = 0;
  @Input()
  public labelledby: string;
  @Input()
  public value?: boolean;
  @Input()
  public key?: string;
  @Output() public readonly toggled = new EventEmitter<KeyValue<string, boolean>>();

  public emitLegacyValue(): void {
    this.toggled.emit({ key: this.key, value: this.value });
  }
  /* end legacy behavior */

  constructor(
    nzConfigService: NzConfigService,
    host: ElementRef<HTMLElement>,
    ngZone: NgZone,
    cdr: ChangeDetectorRef,
    focusMonitor: FocusMonitor,
    @Optional() directionality: Directionality
  ) {
    super(nzConfigService, host, ngZone, cdr, focusMonitor, directionality);
  }

  public ngOnInit(): void {
    super.ngOnInit();

    // detect if we have the legacy operation mode if we have either value or key or any observers on the toggle event
    if (this.value !== undefined || this.key !== undefined || this.toggled.observed) {
      // set the value as passed from above
      this.isChecked = this.value;
      // change the default OnChange to emit the legacy value
      this.registerOnChange(() => {
        this.value = this.isChecked;
        this.emitLegacyValue();
      });
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.value) {
      this.isChecked = changes.value.currentValue;
    }
  }
}
